import { i18n as globalI18n, type I18n } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { formatDocument } from '@/shared/format/document';
import { defineEntitySource } from '@/shared/form/entity-source';
import { getCurrentTenant } from '@/shared/http/client';
import type * as Api from './user.api';
const listUserOptions = async (...args: Parameters<typeof Api.listUserOptions>) =>
  (await import('./user.api')).listUserOptions(...args);
type Item<F extends (...args: never[]) => Promise<{ data: readonly unknown[] }>> = Awaited<
  ReturnType<F>
>['data'][number];
export function userSource(tenant: string | undefined) {
  return defineEntitySource<Item<typeof listUserOptions>>({
    key: ['user', 'source', 'user', tenant ?? ''],
    scope: () => tenant ?? getCurrentTenant(),
    search: async (query, signal) =>
      (
        await listUserOptions(
          { tenant: tenant ?? getCurrentTenant(), name: query || undefined },
          signal,
        )
      ).data,
    toOption: (user) => ({ value: user.id, label: user.name ?? user.id }),
  });
}

/**
 * Operadores do "Copiar permissões" (`useOperatorList`, contexts/OperatorContext.tsx:49-59): o rótulo
 * leva o CPF formatado (menos `N/D`) e "(Usuário desativado)"; o valor é o `id`.
 */
export function userOperatorSource(tenant: string | undefined) {
  return defineEntitySource<Item<typeof listUserOptions>>({
    key: ['user', 'source', 'operator', tenant ?? ''],
    scope: () => tenant ?? getCurrentTenant(),
    search: async (query, signal) =>
      (
        await listUserOptions(
          { tenant: tenant ?? getCurrentTenant(), name: query || undefined },
          signal,
        )
      ).data,
    toOption: (user) => ({ value: user.id, label: operatorLabel(user) }),
  });
}

export function operatorLabel(
  user: {
    readonly name?: string | null | undefined;
    readonly registrationNumber?: string | null | undefined;
    readonly enabled?: boolean | null | undefined;
  },
  i18n: I18n = globalI18n,
) {
  const document =
    user.registrationNumber && user.registrationNumber !== 'N/D'
      ? `- (${formatDocument(user.registrationNumber) ?? user.registrationNumber})`
      : null;
  return [user.name ?? '', document, user.enabled ? null : i18n._(msg`(Usuário desativado)`)]
    .filter((part): part is string => !!part)
    .join(' ');
}
